const RELOAD_COUNT_KEY = 'chunk-error-reload-count';
const RELOAD_TIMESTAMP_KEY = 'chunk-error-reload-timestamp';
const CHUNK_REFRESH_PARAM = '_refresh';
const MAX_RELOAD_ATTEMPTS = 2;
const RESET_INTERVAL_MS = 5 * 60 * 1000;

export function isChunkLoadErrorMessage(message = '') {
  return (
    message.includes("Unexpected token '<'") ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Unable to preload CSS') ||
    message.includes("Cannot read properties of undefined (reading 'default')") ||
    message.includes("Cannot read property 'default' of undefined") ||
    message.includes('_result.default')
  );
}

export function isChunkLoadError(error) {
  const message = error?.message || error?.toString?.() || '';
  return isChunkLoadErrorMessage(message);
}

function isMissingAssetResource(event) {
  const filename = event?.filename || '';
  if (filename.includes('/assets/') && /\.(js|css)(\?|$)/.test(filename)) {
    return true;
  }

  const target = event?.target;
  if (!target || !(target instanceof HTMLElement)) return false;
  const url = target.src || target.href || '';
  return url.includes('/assets/') && /\.(js|css)(\?|$)/.test(url);
}

function readReloadCount() {
  const now = Date.now();
  const lastTimestamp = parseInt(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) || '0', 10);
  if (now - lastTimestamp > RESET_INTERVAL_MS) {
    sessionStorage.setItem(RELOAD_COUNT_KEY, '0');
  }
  return parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
}

/** Returns the count before incrementing. */
export function getAndIncrementReloadCount() {
  const now = Date.now();
  const count = readReloadCount();
  sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
  sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
  return count;
}

export function clearChunkReloadCount() {
  sessionStorage.removeItem(RELOAD_COUNT_KEY);
  sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
}

/** Remove internal cache-bust param from the address bar after a successful load. */
export function stripChunkRefreshParamFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CHUNK_REFRESH_PARAM)) return;
  url.searchParams.delete(CHUNK_REFRESH_PARAM);
  const path = url.pathname + url.search + url.hash;
  history.replaceState(null, '', path);
}

export function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_REFRESH_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

export function tryReloadForChunkError() {
  const reloadCount = getAndIncrementReloadCount();
  if (reloadCount < MAX_RELOAD_ATTEMPTS) {
    reloadWithCacheBust();
    return true;
  }
  return false;
}

/**
 * Auto-reload when a deploy leaves the tab on stale hashed assets.
 * Complements VersionChecker (proactive toast); this is the reactive safety net.
 */
export function registerChunkLoadRecovery() {
  if (typeof window === 'undefined') return;
  stripChunkRefreshParamFromUrl();
  if (window.__chunkLoadRecoveryRegistered) return;
  window.__chunkLoadRecoveryRegistered = true;

  window.addEventListener('error', (event) => {
    if (isChunkLoadErrorMessage(event.message) || isMissingAssetResource(event)) {
      tryReloadForChunkError();
    }
  }, true);

  window.addEventListener('vite:preloadError', (event) => {
    event?.preventDefault?.();
    tryReloadForChunkError();
  });
}
